import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Button,
  Stack,
  IconButton,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import FolderIcon from '@mui/icons-material/Folder';
import DescriptionIcon from '@mui/icons-material/Description';
import RateReviewIcon from '@mui/icons-material/RateReview';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

interface OnboardingOverlayProps {
  open: boolean;
  onComplete: () => void;
}

export default function OnboardingOverlay({ open, onComplete }: OnboardingOverlayProps) {
  const [step, setStep] = useState(0);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const steps = [
    {
      title: 'Welcome to Grant Proposal Writer',
      description: 'Your AI-powered assistant for creating winning grant proposals. This tool helps you generate tailored responses by learning from your organization\'s documents and writing style.',
      icon: <DescriptionIcon sx={{ fontSize: 80, color: theme.palette.primary.main }} />,
    },
    {
      title: 'Context Page',
      description: 'Start by uploading documents about your organization, past proposals, writing samples, and relevant background information. The AI uses these to understand your style and organizational context.',
      icon: <FolderIcon sx={{ fontSize: 80, color: theme.palette.secondary.main }} />,
    },
    {
      title: 'New Proposal Page',
      description: 'Upload your RFP document and the AI will analyze it, extracting questions and requirements. It then generates tailored proposal answers based on your organization\'s context.',
      icon: <DescriptionIcon sx={{ fontSize: 80, color: '#f57c00' }} />,
    },
    {
      title: 'Review, History & Settings',
      description: 'Review and refine generated proposals, access your proposal history, and customize your settings including AI behavior and preferences.',
      icon: <RateReviewIcon sx={{ fontSize: 80, color: '#43a047' }} />,
    },
  ];

  const handleNext = () => {
    if (step < steps.length - 1) {
      setStep(step + 1);
    } else {
      onComplete();
    }
  };

  const handleSkip = () => {
    onComplete();
  };

  return (
    <Dialog
      open={open}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : 4,
          bgcolor: 'background.paper',
          boxShadow: 24,
        },
      }}
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            backdropFilter: 'blur(4px)',
          },
        },
      }}
    >
      <IconButton
        onClick={handleSkip}
        sx={{
          position: 'absolute',
          right: 16,
          top: 16,
          color: 'text.secondary',
          zIndex: 1,
        }}
      >
        <CloseIcon />
      </IconButton>

      <DialogContent sx={{ px: { xs: 3, sm: 5 }, py: { xs: 4, sm: 5 } }}>
        <Stack spacing={4} alignItems="center">
          <Typography
            variant="h4"
            component="h2"
            textAlign="center"
            sx={{
              fontWeight: 700,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              mb: 1,
            }}
          >
            Welcome to Grant Proposal Writer
          </Typography>

          <Box sx={{ display: 'flex', gap: 1.5 }}>
            {steps.map((_, idx) => (
              <Box
                key={idx}
                sx={{
                  width: idx === step ? 32 : 10,
                  height: 10,
                  borderRadius: 5,
                  backgroundColor: idx === step ? theme.palette.primary.main : theme.palette.grey[300],
                  transition: 'all 0.3s ease',
                }}
              />
            ))}
          </Box>

          <Box sx={{ textAlign: 'center', minHeight: 280, display: 'flex', flexDirection: 'column', justifyContent: 'center', py: 2 }}>
            <Box sx={{ mb: 3 }}>
              {steps[step].icon}
            </Box>

            <Typography variant="h5" component="h3" gutterBottom fontWeight={600} color="text.primary">
              {steps[step].title}
            </Typography>

            <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 450, mx: 'auto', mt: 2, lineHeight: 1.7, fontSize: '1.05rem' }}>
              {steps[step].description}
            </Typography>
          </Box>

          <Stack direction="row" spacing={2} sx={{ width: '100%', maxWidth: 450, pt: 2 }}>
            {step > 0 && (
              <Button
                variant="outlined"
                size="large"
                onClick={() => setStep(step - 1)}
                sx={{
                  flex: 1,
                  py: 1.5,
                  borderWidth: 2,
                  '&:hover': {
                    borderWidth: 2,
                  },
                }}
              >
                Back
              </Button>
            )}

            <Button
              variant="contained"
              size="large"
              onClick={handleNext}
              fullWidth={step === 0}
              sx={{
                flex: step > 0 ? 2 : 1,
                py: 1.5,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                boxShadow: '0 4px 14px rgba(102, 126, 234, 0.4)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #5568d3 0%, #5a3a7a 100%)',
                  boxShadow: '0 6px 20px rgba(102, 126, 234, 0.5)',
                },
              }}
              endIcon={step === steps.length - 1 ? null : <ArrowForwardIcon />}
            >
              {step === steps.length - 1 ? 'Get Started' : 'Next'}
            </Button>
          </Stack>

          <Button
            variant="text"
            size="small"
            onClick={handleSkip}
            sx={{
              color: 'text.secondary',
              mt: 1,
              '&:hover': {
                backgroundColor: 'action.hover',
              },
            }}
          >
            Skip tutorial
          </Button>
        </Stack>
      </DialogContent>
    </Dialog>
  );
}
